import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Activity,
  TrendingUp,
  Clock,
  CheckCircle2,
  Mail,
  FileText,
  Calendar,
  Sparkles,
  Zap,
  Brain,
  Timer,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  Target,
  Radio,
  Loader2,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import LiveActivityFeed from '@/components/collaboration/LiveActivityFeed';
import { useRealtimeActivity } from '@/hooks/useRealtimeActivity';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';
import { useLocalization } from '@/hooks/useLocalization';

interface ActivityItem {
  id: string;
  type: 'task' | 'email' | 'meeting' | 'document' | 'ai';
  action: string;
  description: string;
  time: string;
  aiAssisted?: boolean;
  timeSaved?: number;
}

interface ProductivityStat {
  label: string;
  value: number;
  unit: string;
  change: number;
  icon: React.ElementType;
}

interface WeeklyDataPoint {
  day: string;
  tasks: number;
  emails: number;
  meetings: number;
  aiActions: number;
}

interface MonthlyDataPoint {
  week: string;
  productivity: number;
  timeSaved: number;
}

interface AiProductivityStats {
  totalTimeSaved: number;
  tasksAutoGenerated: number;
  emailsAnalyzed: number;
  meetingsSummarized: number;
  productivityIncrease: number;
}

interface Insight {
  tone: 'success' | 'primary' | 'warning';
  title: string;
  body: string;
}

const mockTodayActivities: ActivityItem[] = [
  { id: '1', type: 'ai', action: 'AI Generated', description: 'Meeting minutes for Q4 Planning session', time: '10 mins ago', aiAssisted: true, timeSaved: 25 },
  { id: '2', type: 'task', action: 'Completed', description: 'Review marketing proposal', time: '32 mins ago', aiAssisted: false },
  { id: '3', type: 'email', action: 'AI Analyzed', description: 'Sentiment analysis on 12 emails', time: '1 hour ago', aiAssisted: true, timeSaved: 15 },
  { id: '4', type: 'meeting', action: 'Scheduled', description: 'Stakeholder sync with Product team', time: '2 hours ago', aiAssisted: false },
  { id: '5', type: 'ai', action: 'AI Extracted', description: '5 tasks from meeting transcript', time: '2 hours ago', aiAssisted: true, timeSaved: 20 },
  { id: '6', type: 'document', action: 'Created', description: 'Project status report template', time: '3 hours ago', aiAssisted: true, timeSaved: 30 },
  { id: '7', type: 'task', action: 'Updated', description: 'Changed priority of API integration', time: '4 hours ago', aiAssisted: false },
  { id: '8', type: 'email', action: 'AI Drafted', description: 'Response to vendor inquiry', time: '5 hours ago', aiAssisted: true, timeSaved: 10 },
];

const mockWeeklyData: WeeklyDataPoint[] = [
  { day: 'Mon', tasks: 12, emails: 24, meetings: 3, aiActions: 8 },
  { day: 'Tue', tasks: 15, emails: 18, meetings: 5, aiActions: 12 },
  { day: 'Wed', tasks: 8, emails: 32, meetings: 2, aiActions: 15 },
  { day: 'Thu', tasks: 20, emails: 15, meetings: 4, aiActions: 10 },
  { day: 'Fri', tasks: 18, emails: 22, meetings: 6, aiActions: 14 },
  { day: 'Sat', tasks: 5, emails: 8, meetings: 0, aiActions: 3 },
  { day: 'Sun', tasks: 2, emails: 4, meetings: 0, aiActions: 1 },
];

const mockMonthlyData: MonthlyDataPoint[] = [
  { week: 'Week 1', productivity: 78, timeSaved: 4.2 },
  { week: 'Week 2', productivity: 82, timeSaved: 5.1 },
  { week: 'Week 3', productivity: 85, timeSaved: 6.3 },
  { week: 'Week 4', productivity: 91, timeSaved: 7.8 },
];

const mockAiProductivityStats: AiProductivityStats = {
  totalTimeSaved: 23.5,
  tasksAutoGenerated: 47,
  emailsAnalyzed: 156,
  meetingsSummarized: 12,
  productivityIncrease: 34,
};

const mockInsights: Insight[] = [
  { tone: 'success', title: 'Peak Productivity', body: 'Your most productive hours are 9 AM - 12 PM. AI suggests scheduling complex tasks during this window.' },
  { tone: 'primary', title: 'Time Saved This Week', body: 'AI automation saved you 23.5 hours this week, equivalent to 3 full working days.' },
  { tone: 'warning', title: 'Recommendation', body: 'Enable AI auto-replies for routine emails to save an additional 5+ hours weekly.' },
];

const mockDailyStats = (): ProductivityStat[] => [
  { label: 'Tasks Completed', value: 12, unit: '', change: 20, icon: CheckCircle2 },
  { label: 'Emails Processed', value: 28, unit: '', change: 15, icon: Mail },
  { label: 'Time Saved by AI', value: 2.5, unit: 'h', change: 35, icon: Zap },
  { label: 'Focus Score', value: 87, unit: '%', change: 8, icon: Target },
];

const mockWeeklyStats = (): ProductivityStat[] => [
  { label: 'Tasks Completed', value: 78, unit: '', change: 12, icon: CheckCircle2 },
  { label: 'Emails Processed', value: 123, unit: '', change: 8, icon: Mail },
  { label: 'Time Saved by AI', value: 23.5, unit: 'h', change: 42, icon: Zap },
  { label: 'Avg Focus Score', value: 82, unit: '%', change: 5, icon: Target },
];

const mockMonthlyStats = (): ProductivityStat[] => [
  { label: 'Tasks Completed', value: 312, unit: '', change: 18, icon: CheckCircle2 },
  { label: 'Emails Processed', value: 489, unit: '', change: 22, icon: Mail },
  { label: 'Time Saved by AI', value: 94, unit: 'h', change: 56, icon: Zap },
  { label: 'Productivity Score', value: 91, unit: '%', change: 15, icon: Target },
];

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'task': return CheckCircle2;
    case 'email': return Mail;
    case 'meeting': return Calendar;
    case 'document': return FileText;
    case 'ai': return Sparkles;
    default: return Activity;
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
    case 'task': return 'text-emerald-500 bg-emerald-500/10';
    case 'email': return 'text-blue-500 bg-blue-500/10';
    case 'meeting': return 'text-purple-500 bg-purple-500/10';
    case 'document': return 'text-amber-500 bg-amber-500/10';
    case 'ai': return 'text-primary bg-primary/10';
    default: return 'text-muted-foreground bg-muted';
  }
};

const StatCard = ({ stat }: { stat: ProductivityStat }) => {
  const { tx } = useLocalization();
  const Icon = stat.icon;
  const isPositive = stat.change >= 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <Badge variant={isPositive ? 'default' : 'destructive'} className="gap-1">
            {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {Math.abs(stat.change)}%
          </Badge>
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-foreground">
            {stat.value}{stat.unit}
          </p>
          <p className="text-sm text-muted-foreground">{tx(stat.label)}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const AIProductivityCard = ({ stats, hasData }: { stats: AiProductivityStats; hasData: boolean }) => {
  const { t } = useLocalization();
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          
          {t('AI Productivity Impact')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-muted-foreground">{t('Time Saved')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.totalTimeSaved}h</p>
            <p className="text-xs text-muted-foreground">{t('This week')}</p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">{t('Productivity')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {stats.productivityIncrease >= 0 ? '+' : ''}{stats.productivityIncrease}%
            </p>
            <p className="text-xs text-muted-foreground">{t('Increase')}</p>
          </div>
        </div>

        <div className="space-y-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm text-foreground">{t('Tasks Auto-Generated')}</span>
            </div>
            <span className="font-semibold text-foreground">{stats.tasksAutoGenerated}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-foreground">{t('Emails Analyzed')}</span>
            </div>
            <span className="font-semibold text-foreground">{stats.emailsAnalyzed}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-purple-500" />
              <span className="text-sm text-foreground">{t('Meetings Summarized')}</span>
            </div>
            <span className="font-semibold text-foreground">{stats.meetingsSummarized}</span>
          </div>
        </div>

        <div className="pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">{t('Weekly AI Efficiency')}</p>
          <Progress value={hasData ? Math.min(100, stats.productivityIncrease + 50) : 0} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {hasData
              ? t('AI has helped complete tasks {0}% faster', { 0: stats.productivityIncrease })
              : t('No AI activity recorded yet')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// Shared dashboard renderer — used by BOTH the demo tenant (fed the
// static mock data above) and real tenants (fed data derived from
// tasks_sync/emails_sync/meetings_sync/action_items/ai_outputs).
// Keeping one render path guarantees the two experiences stay
// visually identical.
// ============================================================

interface ActivityDashboardProps {
  dailyStats: ProductivityStat[];
  weeklyStats: ProductivityStat[];
  monthlyStats: ProductivityStat[];
  todayActivities: ActivityItem[];
  weeklyData: WeeklyDataPoint[];
  monthlyData: MonthlyDataPoint[];
  aiProductivityStats: AiProductivityStats;
  hasAiActivity: boolean;
  insights: Insight[];
}

const insightToneClasses: Record<Insight['tone'], string> = {
  success: 'bg-emerald-500/10 border-emerald-500/20',
  primary: 'bg-primary/10 border-primary/20',
  warning: 'bg-amber-500/10 border-amber-500/20',
};

const ActivityDashboard = ({
  dailyStats,
  weeklyStats,
  monthlyStats,
  todayActivities,
  weeklyData,
  monthlyData,
  aiProductivityStats,
  hasAiActivity,
  insights,
}: ActivityDashboardProps) => {
  const { tx, t } = useLocalization();
  const [period, setPeriod] = useState('today');

  const getStats = () => {
    switch (period) {
      case 'today': return dailyStats;
      case 'week': return weeklyStats;
      case 'month': return monthlyStats;
      default: return dailyStats;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('Activity')}</h1>
          <p className="text-muted-foreground">{t('Track your daily productivity and AI impact')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">{t('Export Report')}</Button>
        </div>
      </div>

      {/* Period Tabs */}
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList>
          <TabsTrigger value="today">{t('Today')}</TabsTrigger>
          <TabsTrigger value="week">{t('This Week')}</TabsTrigger>
          <TabsTrigger value="month">{t('This Month')}</TabsTrigger>
        </TabsList>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
          {getStats().map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Activity Timeline - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            <TabsContent value="today" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    
                    {t('Today\'s Activity')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {todayActivities.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">{t('No activity recorded yet today.')}</p>
                  ) : (
                    <div className="space-y-4">
                      {todayActivities.map((activity) => {
                        const Icon = getActivityIcon(activity.type);
                        const colorClass = getActivityColor(activity.type);

                        return (
                          <div key={activity.id} className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-foreground">{activity.action}</span>
                                {activity.aiAssisted && (
                                  <Badge variant="secondary" className="gap-1 text-xs">
                                    <Sparkles className="h-3 w-3" />
                                    
                                    {t('AI')}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">{tx(activity.description)}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs text-muted-foreground">{activity.time}</span>
                                {activity.timeSaved && (
                                  <span className="text-xs text-emerald-500 flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {activity.timeSaved}  {t('min saved')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="week" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    
                    {t('Weekly Activity Overview')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="day" className="text-muted-foreground" />
                        <YAxis className="text-muted-foreground" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="tasks" name="Tasks" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="emails" name="Emails" fill="hsl(220 90% 60%)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="aiActions" name="AI Actions" fill="hsl(280 80% 60%)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="month" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    
                    {t('Monthly Productivity Trend')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="week" className="text-muted-foreground" />
                        <YAxis className="text-muted-foreground" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="productivity"
                          name="Productivity %"
                          stroke="hsl(var(--primary))"
                          fill="hsl(var(--primary) / 0.2)"
                        />
                        <Area
                          type="monotone"
                          dataKey="timeSaved"
                          name="Hours Saved"
                          stroke="hsl(142 70% 45%)"
                          fill="hsl(142 70% 45% / 0.2)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>

          {/* AI Productivity Card - Right sidebar */}
          <div className="space-y-6">
            {/* Live Team Activity */}
            <LiveTeamActivityCard />

            <AIProductivityCard stats={aiProductivityStats} hasData={hasAiActivity} />

            {/* AI Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-5 w-5 text-primary" />
                  
                  {t('AI Insights')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.length > 0 ? (
                  insights.map((insight) => (
                    <div key={insight.title} className={`p-3 rounded-lg border ${insightToneClasses[insight.tone]}`}>
                      <p className="text-sm text-foreground font-medium">{tx(insight.title)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{insight.body}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">{t('No AI insights yet. Insights appear once AI activity has been recorded.')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

const LiveTeamActivityCard = () => {
  const { t } = useLocalization();
  const { activities } = useRealtimeActivity();
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Radio className="h-4 w-4 text-success animate-pulse" />
          
          {t('Live Team Activity')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <LiveActivityFeed activities={activities} />
      </CardContent>
    </Card>
  );
};

// ============================================================
// Real-tenant data layer. Every number here is derived from tables
// that are already populated by connectors/AI functions — no new
// tables, unlike Strategy (which needed leadership-input tables
// because revenue/ROI have no synced source). Two metrics have no
// direct source and are estimated, each documented at its formula:
//   - "Time saved by AI" — AI_MINUTES_SAVED constants below, applied
//     per AI-touched record (meeting summarized, action item
//     extracted, AI output generated). These are estimates, not
//     measured durations.
//   - "Focus / Productivity score" — % of tasks touched in the period
//     that reached a done status (completion rate), not a real focus
//     metric — there's no attention/session data to measure focus.
// ============================================================

const AI_MINUTES_SAVED = {
  meetingSummary: 25,
  actionItem: 20,
  aiOutput: 30,
} as const;

const DONE_STATUS_RE = /done|closed|resolved|complete/i;

interface DbTaskRow { id: string; title: string; status: string | null; updated_at: string }
interface DbEmailRow { id: string; received_at: string | null }
interface DbMeetingRow { id: string; title: string; start_time: string | null; ai_summary: string | null; updated_at: string }
interface DbActionItemRow { id: string; title: string; created_at: string; source_meeting_id: string | null; source_email_id: string | null }
interface DbAiOutputRow { id: string; title: string; source_module: string; created_at: string }

const startOfDay = (d: Date) => { const c = new Date(d); c.setHours(0, 0, 0, 0); return c; };
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
const inRange = (iso: string | null, start: Date, end: Date) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t < end.getTime();
};

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(1, Math.round(diffMs / 60000));
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const pctChange = (current: number, previous: number) =>
  previous > 0 ? Math.round(((current - previous) / previous) * 100) : (current > 0 ? 100 : 0);

const RealActivityDashboard = () => {
  const [tasks, setTasks] = useState<DbTaskRow[]>([]);
  const [emails, setEmails] = useState<DbEmailRow[]>([]);
  const [meetings, setMeetings] = useState<DbMeetingRow[]>([]);
  const [actionItems, setActionItems] = useState<DbActionItemRow[]>([]);
  const [aiOutputs, setAiOutputs] = useState<DbAiOutputRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const cutoff = daysAgo(56).toISOString(); // 8 weeks: covers today/week/month plus their prior-period comparisons

    const [tasksRes, emailsRes, meetingsRes, actionItemsRes, aiOutputsRes] = await Promise.all([
      supabase.from('tasks_sync').select('id, title, status, updated_at').gte('updated_at', cutoff),
      supabase.from('emails_sync').select('id, received_at').gte('received_at', cutoff),
      supabase.from('meetings_sync').select('id, title, start_time, ai_summary, updated_at').gte('updated_at', cutoff),
      supabase.from('action_items').select('id, title, created_at, source_meeting_id, source_email_id').gte('created_at', cutoff),
      supabase.from('ai_outputs').select('id, title, source_module, created_at').gte('created_at', cutoff),
    ]);

    if (!tasksRes.error) setTasks((tasksRes.data ?? []) as DbTaskRow[]);
    if (!emailsRes.error) setEmails((emailsRes.data ?? []) as DbEmailRow[]);
    if (!meetingsRes.error) setMeetings((meetingsRes.data ?? []) as DbMeetingRow[]);
    if (!actionItemsRes.error) setActionItems((actionItemsRes.data ?? []) as DbActionItemRow[]);
    if (!aiOutputsRes.error) setAiOutputs((aiOutputsRes.data ?? []) as DbAiOutputRow[]);

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const computed = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const yesterdayStart = startOfDay(daysAgo(1));
    const weekStart = daysAgo(7);
    const prevWeekStart = daysAgo(14);
    const monthStart = daysAgo(30);
    const prevMonthStart = daysAgo(60);

    const completedTasks = tasks.filter((t) => t.status && DONE_STATUS_RE.test(t.status));
    const aiTouchedMeetings = meetings.filter((m) => !!m.ai_summary);

    const countInRange = <T,>(rows: T[], get: (r: T) => string | null, start: Date, end: Date) =>
      rows.filter((r) => inRange(get(r), start, end)).length;

    const buildStats = (
      labels: [string, string, string, string],
      start: Date,
      end: Date,
      prevStart: Date,
      prevEnd: Date,
    ): ProductivityStat[] => {
      const tasksNow = countInRange(completedTasks, (t) => t.updated_at, start, end);
      const tasksPrev = countInRange(completedTasks, (t) => t.updated_at, prevStart, prevEnd);
      const emailsNow = countInRange(emails, (e) => e.received_at, start, end);
      const emailsPrev = countInRange(emails, (e) => e.received_at, prevStart, prevEnd);

      const aiActionsNow = countInRange(actionItems, (a) => a.created_at, start, end)
        + countInRange(aiTouchedMeetings, (m) => m.updated_at, start, end)
        + countInRange(aiOutputs, (a) => a.created_at, start, end);
      const aiActionsPrev = countInRange(actionItems, (a) => a.created_at, prevStart, prevEnd)
        + countInRange(aiTouchedMeetings, (m) => m.updated_at, prevStart, prevEnd)
        + countInRange(aiOutputs, (a) => a.created_at, prevStart, prevEnd);

      const timeSavedNowMin = countInRange(actionItems, (a) => a.created_at, start, end) * AI_MINUTES_SAVED.actionItem
        + countInRange(aiTouchedMeetings, (m) => m.updated_at, start, end) * AI_MINUTES_SAVED.meetingSummary
        + countInRange(aiOutputs, (a) => a.created_at, start, end) * AI_MINUTES_SAVED.aiOutput;
      const timeSavedPrevMin = countInRange(actionItems, (a) => a.created_at, prevStart, prevEnd) * AI_MINUTES_SAVED.actionItem
        + countInRange(aiTouchedMeetings, (m) => m.updated_at, prevStart, prevEnd) * AI_MINUTES_SAVED.meetingSummary
        + countInRange(aiOutputs, (a) => a.created_at, prevStart, prevEnd) * AI_MINUTES_SAVED.aiOutput;

      // Focus/productivity score: % of tasks touched in the period that reached "done" — a completion-rate proxy, not a measured focus signal.
      const touchedNow = countInRange(tasks, (t) => t.updated_at, start, end);
      const touchedPrev = countInRange(tasks, (t) => t.updated_at, prevStart, prevEnd);
      const focusNow = touchedNow > 0 ? Math.round((tasksNow / touchedNow) * 100) : 0;
      const focusPrev = touchedPrev > 0 ? Math.round((tasksPrev / touchedPrev) * 100) : 0;

      return [
        { label: labels[0], value: tasksNow, unit: '', change: pctChange(tasksNow, tasksPrev), icon: CheckCircle2 },
        { label: labels[1], value: emailsNow, unit: '', change: pctChange(emailsNow, emailsPrev), icon: Mail },
        { label: labels[2], value: Math.round((timeSavedNowMin / 60) * 10) / 10, unit: 'h', change: pctChange(aiActionsNow, aiActionsPrev), icon: Zap },
        { label: labels[3], value: focusNow, unit: '%', change: pctChange(focusNow, focusPrev), icon: Target },
      ];
    };

    const dailyStats = buildStats(
      ['Tasks Completed', 'Emails Processed', 'Time Saved by AI', 'Focus Score'],
      todayStart, now, yesterdayStart, todayStart,
    );
    const weeklyStats = buildStats(
      ['Tasks Completed', 'Emails Processed', 'Time Saved by AI', 'Avg Focus Score'],
      weekStart, now, prevWeekStart, weekStart,
    );
    const monthlyStats = buildStats(
      ['Tasks Completed', 'Emails Processed', 'Time Saved by AI', 'Productivity Score'],
      monthStart, now, prevMonthStart, monthStart,
    );

    // Rolling 7-day bar chart, oldest to newest.
    const weekdayLabel = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'short' });
    const weeklyData: WeeklyDataPoint[] = Array.from({ length: 7 }).map((_, i) => {
      const dayStart = startOfDay(daysAgo(6 - i));
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return {
        day: weekdayLabel(dayStart),
        tasks: countInRange(completedTasks, (t) => t.updated_at, dayStart, dayEnd),
        emails: countInRange(emails, (e) => e.received_at, dayStart, dayEnd),
        meetings: countInRange(meetings, (m) => m.start_time, dayStart, dayEnd),
        aiActions: countInRange(actionItems, (a) => a.created_at, dayStart, dayEnd)
          + countInRange(aiTouchedMeetings, (m) => m.updated_at, dayStart, dayEnd)
          + countInRange(aiOutputs, (a) => a.created_at, dayStart, dayEnd),
      };
    });

    // Rolling 4-week area chart, oldest to newest.
    const monthlyData: MonthlyDataPoint[] = Array.from({ length: 4 }).map((_, i) => {
      const wStart = daysAgo((4 - i) * 7);
      const wEnd = daysAgo((3 - i) * 7);
      const wTasksDone = countInRange(completedTasks, (t) => t.updated_at, wStart, wEnd);
      const wTouched = countInRange(tasks, (t) => t.updated_at, wStart, wEnd);
      const wTimeSavedMin = countInRange(actionItems, (a) => a.created_at, wStart, wEnd) * AI_MINUTES_SAVED.actionItem
        + countInRange(aiTouchedMeetings, (m) => m.updated_at, wStart, wEnd) * AI_MINUTES_SAVED.meetingSummary
        + countInRange(aiOutputs, (a) => a.created_at, wStart, wEnd) * AI_MINUTES_SAVED.aiOutput;
      return {
        week: `Week ${i + 1}`,
        productivity: wTouched > 0 ? Math.round((wTasksDone / wTouched) * 100) : 0,
        timeSaved: Math.round((wTimeSavedMin / 60) * 10) / 10,
      };
    });

    // Today's activity timeline — merge real events across sources, newest first.
    const timeline: (ActivityItem & { ts: string })[] = [];
    for (const a of actionItems) {
      if (!inRange(a.created_at, todayStart, now)) continue;
      if (!a.source_meeting_id && !a.source_email_id) continue;
      timeline.push({
        id: `action-${a.id}`, type: 'ai', action: 'AI Extracted', ts: a.created_at,
        description: a.title, time: timeAgo(a.created_at), aiAssisted: true, timeSaved: AI_MINUTES_SAVED.actionItem,
      });
    }
    for (const m of aiTouchedMeetings) {
      if (!inRange(m.updated_at, todayStart, now)) continue;
      timeline.push({
        id: `meeting-ai-${m.id}`, type: 'ai', action: 'AI Generated', ts: m.updated_at,
        description: `Meeting minutes for ${m.title}`, time: timeAgo(m.updated_at), aiAssisted: true, timeSaved: AI_MINUTES_SAVED.meetingSummary,
      });
    }
    for (const a of aiOutputs) {
      if (!inRange(a.created_at, todayStart, now)) continue;
      timeline.push({
        id: `output-${a.id}`, type: 'document', action: 'AI Generated', ts: a.created_at,
        description: a.title, time: timeAgo(a.created_at), aiAssisted: true, timeSaved: AI_MINUTES_SAVED.aiOutput,
      });
    }
    for (const t of completedTasks) {
      if (!inRange(t.updated_at, todayStart, now)) continue;
      timeline.push({ id: `task-${t.id}`, type: 'task', action: 'Completed', ts: t.updated_at, description: t.title, time: timeAgo(t.updated_at), aiAssisted: false });
    }
    for (const m of meetings) {
      if (!inRange(m.start_time, todayStart, now)) continue;
      timeline.push({ id: `meeting-${m.id}`, type: 'meeting', action: 'Scheduled', ts: m.start_time as string, description: m.title, time: timeAgo(m.start_time as string), aiAssisted: false });
    }
    timeline.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    const todayActivities: ActivityItem[] = timeline.slice(0, 8).map(({ ts: _ts, ...item }) => item);

    // AI Productivity Impact card — scoped to the current week, mirrors the "This week" label under Time Saved.
    const aiActionsThisWeek = countInRange(actionItems, (a) => a.created_at, weekStart, now)
      + countInRange(aiTouchedMeetings, (m) => m.updated_at, weekStart, now)
      + countInRange(aiOutputs, (a) => a.created_at, weekStart, now);
    const timeSavedThisWeekMin = countInRange(actionItems, (a) => a.created_at, weekStart, now) * AI_MINUTES_SAVED.actionItem
      + countInRange(aiTouchedMeetings, (m) => m.updated_at, weekStart, now) * AI_MINUTES_SAVED.meetingSummary
      + countInRange(aiOutputs, (a) => a.created_at, weekStart, now) * AI_MINUTES_SAVED.aiOutput;

    const aiProductivityStats: AiProductivityStats = {
      totalTimeSaved: Math.round((timeSavedThisWeekMin / 60) * 10) / 10,
      tasksAutoGenerated: countInRange(actionItems, (a) => a.created_at, weekStart, now),
      emailsAnalyzed: countInRange(emails, (e) => e.received_at, weekStart, now),
      meetingsSummarized: countInRange(aiTouchedMeetings, (m) => m.updated_at, weekStart, now),
      productivityIncrease: weeklyStats[3].change,
    };

    // AI Insights — only genuinely-derived observations; no invented copy.
    const insights: Insight[] = [];
    if (aiActionsThisWeek > 0) {
      const peak = weeklyData.reduce((best, d) => {
        const total = d.tasks + d.emails + d.aiActions;
        const bestTotal = best.tasks + best.emails + best.aiActions;
        return total > bestTotal ? d : best;
      }, weeklyData[0]);
      insights.push({
        tone: 'success',
        title: 'Peak Activity Day',
        body: `${peak.day} was your busiest day this week — consider scheduling complex work on similar days.`,
      });
      insights.push({
        tone: 'primary',
        title: 'Time Saved This Week',
        body: `AI automation saved an estimated ${aiProductivityStats.totalTimeSaved}h this week.`,
      });
    }

    return { dailyStats, weeklyStats, monthlyStats, weeklyData, monthlyData, todayActivities, aiProductivityStats, aiActionsThisWeek, insights };
  }, [tasks, emails, meetings, actionItems, aiOutputs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <ActivityDashboard
      dailyStats={computed.dailyStats}
      weeklyStats={computed.weeklyStats}
      monthlyStats={computed.monthlyStats}
      todayActivities={computed.todayActivities}
      weeklyData={computed.weeklyData}
      monthlyData={computed.monthlyData}
      aiProductivityStats={computed.aiProductivityStats}
      hasAiActivity={computed.aiActionsThisWeek > 0}
      insights={computed.insights}
    />
  );
};

const ActivityView = () => {
  const { isDemoTenant } = useTenant();

  if (!isDemoTenant) {
    return <RealActivityDashboard />;
  }

  return (
    <ActivityDashboard
      dailyStats={mockDailyStats()}
      weeklyStats={mockWeeklyStats()}
      monthlyStats={mockMonthlyStats()}
      todayActivities={mockTodayActivities}
      weeklyData={mockWeeklyData}
      monthlyData={mockMonthlyData}
      aiProductivityStats={mockAiProductivityStats}
      hasAiActivity
      insights={mockInsights}
    />
  );
};

export default ActivityView;
